import { useEffect, useMemo, useRef, useState } from "react"
import type { CurrentNoteVersionSnapshot, LexicalText, Note, NoteVersionSnapshot } from "@/schema"
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Skeleton } from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { ChevronRight, MoreHorizontal, RotateCcw, X } from "lucide-react"
import { toast } from "sonner"

import { useInfiniteNoteVersions, useRestoreNoteVersion } from "@/hooks/api/notes"
import { ConfirmDialog } from "@/components/dialogs"

interface NoteVersionHistoryPanelProps {
  noteId: string
  isLocked: boolean
  initialVersion?: NoteVersionSnapshot & { is_current?: boolean }
  onPreview: (content: LexicalText, isHistorical: boolean, versionId?: string) => void
  onClose: () => void
  onRestored: (note: Note) => void
}

function isHistoricalSnapshot(
  snapshot: CurrentNoteVersionSnapshot | NoteVersionSnapshot
): snapshot is NoteVersionSnapshot {
  return "id" in snapshot && typeof snapshot.id === "string"
}

export function NoteVersionHistoryPanel({
  noteId,
  isLocked,
  initialVersion,
  onPreview,
  onClose,
  onRestored,
}: NoteVersionHistoryPanelProps) {
  const versionsQuery = useInfiniteNoteVersions(noteId)
  const restoreVersion = useRestoreNoteVersion(noteId)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [restoreCandidate, setRestoreCandidate] = useState<NoteVersionSnapshot>()
  const appliedInitialVersionId = useRef<string | undefined>(undefined)

  const snapshots = useMemo(() => {
    if (!versionsQuery.data?.current) return []

    const historicalVersions = [...versionsQuery.data.versions]
    if (
      initialVersion &&
      !initialVersion.is_current &&
      !historicalVersions.some((version) => version.id === initialVersion.id)
    ) {
      historicalVersions.push(initialVersion)
      historicalVersions.sort(
        (first, second) => new Date(second.created_at).getTime() - new Date(first.created_at).getTime()
      )
    }

    return [versionsQuery.data.current, ...historicalVersions]
  }, [initialVersion, versionsQuery.data])

  useEffect(() => {
    if (!initialVersion || appliedInitialVersionId.current === initialVersion.id) return

    const initialIndex = initialVersion.is_current
      ? 0
      : snapshots.findIndex((snapshot) => isHistoricalSnapshot(snapshot) && snapshot.id === initialVersion.id)
    if (initialIndex < 0) return

    setSelectedIndex(initialIndex)
    appliedInitialVersionId.current = initialVersion.id
  }, [initialVersion, snapshots])

  useEffect(() => {
    const selectedSnapshot = snapshots[selectedIndex]
    if (!selectedSnapshot) return

    const versionId = isHistoricalSnapshot(selectedSnapshot) ? selectedSnapshot.id : undefined
    onPreview(selectedSnapshot.content, selectedIndex > 0, versionId)
  }, [onPreview, selectedIndex, snapshots])

  function closePanel() {
    const currentSnapshot = snapshots[0]
    if (currentSnapshot) onPreview(currentSnapshot.content, false)
    onClose()
  }

  function confirmRestore() {
    if (!restoreCandidate) return

    restoreVersion.mutate(restoreCandidate.id, {
      onSuccess: (note) => {
        setRestoreCandidate(undefined)
        onRestored(note)
        onClose()
        toast.success("Note version restored")
      },
    })
  }

  return (
    <>
      <aside className="bg-muted/30 flex w-72 shrink-0 flex-col border-l">
        <div className="flex h-12 shrink-0 items-center justify-between border-b px-4">
          <h2 className="text-sm font-semibold">Version history</h2>
          <Button variant="ghost" size="iconXs" onClick={closePanel} aria-label="Close version history">
            <X />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {versionsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : versionsQuery.isError ? (
            <div className="py-8 text-center">
              <p className="text-sm font-medium">Unable to load version history</p>
              <Button variant="outline" size="xs" className="mt-3" onClick={() => versionsQuery.refetch()}>
                Try again
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {snapshots.map((snapshot, index) => {
                const historicalVersion = index > 0 && isHistoricalSnapshot(snapshot) ? snapshot : undefined
                const selected = selectedIndex === index

                return (
                  <div
                    key={historicalVersion?.id ?? "latest"}
                    className={cn(
                      "group flex items-start rounded-xl border border-transparent",
                      selected && "border-border bg-background shadow-sm"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className="flex min-w-0 flex-1 items-start gap-1.5 px-3 py-3 text-left"
                    >
                      <ChevronRight className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">
                          {format(new Date(snapshot.created_at), "MMM d, h:mm a")}
                        </span>
                        {index === 0 ? (
                          <span className="text-muted-foreground mt-0.5 block text-xs">Current version</span>
                        ) : null}
                        <span className="text-muted-foreground mt-1 block truncate text-xs">
                          {snapshot.actor?.name ?? "Unknown user"}
                        </span>
                      </span>
                    </button>

                    {historicalVersion && selected ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="iconXs"
                            className="mt-2 mr-2 shrink-0"
                            aria-label="Version actions"
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-0">
                          <DropdownMenuItem
                            className="px-1.5 py-1 text-xs"
                            disabled={isLocked}
                            onSelect={() => setRestoreCandidate(historicalVersion)}
                          >
                            <RotateCcw className="size-3!" />
                            Restore this version
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                )
              })}

              {versionsQuery.hasNextPage ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  disabled={versionsQuery.isFetchingNextPage}
                  onClick={() => versionsQuery.fetchNextPage()}
                >
                  {versionsQuery.isFetchingNextPage ? "Loading…" : "Load older versions"}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </aside>

      <ConfirmDialog
        open={Boolean(restoreCandidate)}
        onOpenChange={(open) => !open && setRestoreCandidate(undefined)}
        title={"Restore this note version?"}
        description={
          "The selected body will become the latest version. The current body will remain available in version history."
        }
        dismiss={restoreVersion.isPending ? "none" : "explicit"}
        cancel={{ onClick: () => setRestoreCandidate(undefined), disabled: restoreVersion.isPending }}
        action={{ label: "Restore version", onClick: confirmRestore, pending: restoreVersion.isPending }}
      />
    </>
  )
}
